import { axiosGet } from '@/api/handlers';
import {
  AutoComplete,
  type AutoCompleteOptionType,
  Box,
  Button,
  Col,
  Divider,
  Flex,
  Form,
  HintIcon,
  Input,
  Loading,
  RemovableCard,
  Row,
  SelectField,
  Text,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import { useProjectFormContext } from '@/features/project/_hooks/form-context';
import { useMessage } from '@/providers';
import { handleKeyDown } from '@/utils';
import { type FC, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  LawArticlesModel,
  LawParagraphsModel,
  LawsModel,
} from '../../../_models/law';

type LegalOptionType = AutoCompleteOptionType & {
  number: string;
  name: string;
};

export const LegalBasesArrayField: FC = () => {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    resetField,
  } = useProjectFormContext();
  const { t } = useTranslation();
  const { openMessage } = useMessage();

  const [legalSearch, setLegalSearch] = useState('');
  const [legalOptions, setLegalOptions] = useState<LegalOptionType[]>([]);
  const [articleOptionsMap, setArticleOptionsMap] = useState<
    Record<string, Array<{ label: string; value: string | number }>>
  >({});
  const [paragraphOptionsMap, setParagraphOptionsMap] = useState<
    Record<
      string,
      Record<string, Array<{ label: string; value: string | number }>>
    >
  >({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [articleOption, setArticleOption] = useState<
    Record<string, { label: string; value: string | number }>
  >({});
  const [paragraphOption, setParagraphOption] = useState<
    Record<string, Record<string, { label: string; value: string | number }>>
  >({});

  const {
    fields: legalBasisFields,
    append: appendLegalBasis,
    remove: removeLegalBasis,
  } = useFieldArray({
    control,
    name: 'data.legalBases',
  });

  const generateLegalOptions = async () => {
    const legalSuggestions = LawsModel.safeParse(
      await axiosGet('/api/government-api/laws/', {
        lawNameMatcher: legalSearch,
      }),
    );
    if (!legalSuggestions.success) {
      await openMessage('error', t('failed-fetch-law'));
      return;
    }
    setLegalOptions(
      legalSuggestions.data.map((legal) => {
        return {
          value: legal.id,
          number: legal.number,
          name: legal.name,
          label: (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Text text={legal.name} />
            </div>
          ),
        };
      }),
    );
  };

  const generateLawArticleOptions = async (lawId: string) => {
    try {
      setLoading((prev) => ({ ...prev, [lawId]: true }));
      const lawArticles = LawArticlesModel.safeParse(
        await axiosGet(`/api/government-api/laws/${lawId}/articles/`),
      );
      if (!lawArticles.success) {
        await openMessage('error', t('failed-fetch-law'));
        setLoading({});
        return;
      }
      setArticleOptionsMap({
        ...articleOptionsMap,
        [lawId]: lawArticles.data.articleTitles.map((articleTitle) => {
          return {
            value: articleTitle,
            label: articleTitle,
          };
        }),
      });
      setLoading((prev) => ({ ...prev, [lawId]: false }));
    } catch {
      setLoading({});
    }
  };

  const generateLawParagraphOptions = async (
    lawId: string,
    article: string,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, [`${lawId}-${article}`]: true }));
      const lawParagraphs = LawParagraphsModel.safeParse(
        await axiosGet(
          `/api/government-api/laws/${lawId}/articles/${article}/paragraphs/`,
        ),
      );
      if (!lawParagraphs.success) {
        await openMessage('error', t('failed-fetch-law'));
        setLoading({});
        return;
      }
      setParagraphOptionsMap({
        ...paragraphOptionsMap,
        [lawId]: {
          ...paragraphOptionsMap[lawId],
          [article]: lawParagraphs.data.paragraphs.map((paragraph) => {
            return {
              value: paragraph,
              label: paragraph,
            };
          }),
        },
      });
      setLoading((prev) => ({ ...prev, [`${lawId}-${article}`]: false }));
    } catch {
      setLoading({});
    }
  };

  return (
    <Form.Item>
      <Row gutter={[24, 16]}>
        {legalBasisFields.map((item, idx) => {
          const lawId = watch(`data.legalBases.${idx}.id`);
          const article = watch(`data.legalBases.${idx}.article`);
          const isManualInput = watch(`data.legalBases.${idx}.isManualInput`);
          return (
            <Col key={item.id} span={24}>
              <RemovableCard
                onRemove={() => {
                  removeLegalBasis(idx);
                }}
              >
                <Row gutter={[24, 0]}>
                  <Col span={12}>
                    <Row gutter={[16, 8]}>
                      <Col span={24}>
                        <TextAreaInputField
                          name={`data.legalBases.${idx}.name`}
                          label={t('law-name')}
                          rows={5}
                          control={control}
                          errors={errors}
                          required
                          style={{ marginBottom: 16 }}
                          disabled={!isManualInput}
                        />
                      </Col>
                      <Col span={12}>
                        <TextInputField
                          name={`data.legalBases.${idx}.id`}
                          autocompleteKey="legalBasis.id"
                          label={t('law-id')}
                          control={control}
                          errors={errors}
                          marginBottom={0}
                          disabled={!isManualInput}
                          shouldCoexistOnChange
                          fallbackValue=""
                          onChange={() => {
                            resetField(`data.legalBases.${idx}.article`, {
                              defaultValue: null,
                            });
                            resetField(`data.legalBases.${idx}.paragraph`, {
                              defaultValue: null,
                            });
                          }}
                        />
                      </Col>
                      <Col span={12}>
                        <TextInputField
                          name={`data.legalBases.${idx}.number`}
                          autocompleteKey="legalBasis.number"
                          label={t('law-number')}
                          control={control}
                          errors={errors}
                          marginBottom={0}
                          disabled={!isManualInput}
                          fallbackValue=""
                        />
                      </Col>
                    </Row>
                  </Col>
                  <Col span={12}>
                    <Row gutter={[16, 16]}>
                      <Col span={24}>
                        <SelectField
                          name={`data.legalBases.${idx}.article`}
                          label={t('law-article')}
                          control={control}
                          errors={errors}
                          disabled={!lawId}
                          {...(lawId &&
                            loading[lawId] && {
                              notFoundContent: <Loading />,
                            })}
                          shouldCoexistOnChange
                          onChange={async (article) => {
                            resetField(`data.legalBases.${idx}.paragraph`, {
                              defaultValue: null,
                            });
                            if (lawId && article) {
                              await generateLawParagraphOptions(lawId, article);
                            }
                          }}
                          onDropdownVisibleChange={async (open) => {
                            if (open && lawId && !loading[lawId]) {
                              await generateLawArticleOptions(lawId);
                            }
                          }}
                          options={lawId ? articleOptionsMap[lawId] : []}
                          wrapperStyle={{ flex: 1, marginBottom: 0 }}
                          {...(lawId && {
                            dropdownRender: (menu) => (
                              <>
                                {menu}
                                <Divider
                                  style={{ marginTop: 8, marginBottom: 8 }}
                                />
                                <Flex gap={8} style={{ padding: 8 }}>
                                  <Input
                                    value={articleOption[lawId]?.value ?? ''}
                                    onChange={({ target: { value } }) => {
                                      setArticleOption({
                                        [lawId]: {
                                          label: value,
                                          value,
                                        },
                                      });
                                    }}
                                  />
                                  <Button
                                    disabled={
                                      !articleOption[lawId]?.value
                                        .toString()
                                        .trim().length
                                    }
                                    onClick={() => {
                                      const option = articleOption[lawId];
                                      setArticleOptionsMap((prev) => ({
                                        ...prev,
                                        ...(option && {
                                          [lawId]: [option],
                                        }),
                                      }));
                                      setValue(
                                        `data.legalBases.${idx}.article`,
                                        option?.value.toString(),
                                        { shouldDirty: true },
                                      );
                                      resetField(
                                        `data.legalBases.${idx}.paragraph`,
                                        { defaultValue: null },
                                      );
                                      setArticleOption({});
                                    }}
                                  >
                                    {t('manual-input')}
                                  </Button>
                                </Flex>
                              </>
                            ),
                          })}
                        />
                      </Col>
                      <Col span={24}>
                        <SelectField
                          name={`data.legalBases.${idx}.paragraph`}
                          label={t('law-paragraph')}
                          control={control}
                          errors={errors}
                          disabled={!lawId || !article}
                          {...(lawId &&
                            article &&
                            loading[`${lawId}-${article}`] && {
                              notFoundContent: <Loading />,
                            })}
                          onDropdownVisibleChange={async (open) => {
                            if (
                              open &&
                              lawId &&
                              article &&
                              !loading[`${lawId}-${article}`]
                            ) {
                              await generateLawParagraphOptions(lawId, article);
                            }
                          }}
                          options={
                            lawId && paragraphOptionsMap[lawId]
                              ? paragraphOptionsMap[lawId]?.[article ?? '']
                              : []
                          }
                          wrapperStyle={{ flex: 1, marginBottom: 0 }}
                          {...(lawId &&
                            article && {
                              dropdownRender: (menu) => (
                                <>
                                  {menu}
                                  <Divider
                                    style={{ marginTop: 8, marginBottom: 8 }}
                                  />
                                  <Flex gap={8} style={{ padding: 8 }}>
                                    <Input
                                      value={
                                        paragraphOption[lawId]?.[article]
                                          ?.value ?? ''
                                      }
                                      onChange={({ target: { value } }) => {
                                        setParagraphOption({
                                          [lawId]: {
                                            [article]: {
                                              label: value,
                                              value,
                                            },
                                          },
                                        });
                                      }}
                                    />
                                    <Button
                                      disabled={
                                        !paragraphOption[lawId]?.[
                                          article
                                        ]?.value
                                          .toString()
                                          .trim().length
                                      }
                                      onClick={() => {
                                        const option =
                                          paragraphOption[lawId]?.[article];
                                        setParagraphOptionsMap((prev) => ({
                                          ...prev,
                                          ...(option && {
                                            [lawId]: {
                                              ...prev[lawId],
                                              [article]: [option],
                                            },
                                          }),
                                        }));
                                        setValue(
                                          `data.legalBases.${idx}.paragraph`,
                                          option?.value.toString(),
                                          { shouldDirty: true },
                                        );
                                        setParagraphOption({});
                                      }}
                                    >
                                      {t('manual-input')}
                                    </Button>
                                  </Flex>
                                </>
                              ),
                            })}
                        />
                      </Col>
                      <Col span={24}>
                        <TextInputField
                          name={`data.legalBases.${idx}.paragraphSubIndex`}
                          autocompleteKey="legalBasis.paragraphSubIndex"
                          label={t('law-paragraph-sub-index')}
                          control={control}
                          errors={errors}
                          marginBottom={0}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </RemovableCard>
            </Col>
          );
        })}

        <Col span={12}>
          <Flex gap="middle" align="center">
            <Flex gap={4} align="center">
              <Text
                text={t('add-by-search')}
                color="secondary"
                weight="regular"
              />
              <HintIcon hint={t('hint-legal-basis-search')} />
            </Flex>
            <Box flex={1}>
              <AutoComplete
                onChange={(e) => {
                  setLegalSearch(e);
                }}
                value={legalSearch}
                options={legalOptions}
                onKeyDown={(e) => {
                  handleKeyDown(e, generateLegalOptions);
                }}
                onSearch={generateLegalOptions}
                onSelect={async (selectedLegalId: string) => {
                  const legalOption = legalOptions.find(
                    (legalOption) => legalOption.value === selectedLegalId,
                  );
                  if (legalOption) {
                    appendLegalBasis({
                      id: selectedLegalId,
                      number: legalOption.number,
                      name: legalOption.name,
                      article: null,
                      paragraph: null,
                      paragraphSubIndex: null,
                      isManualInput: false,
                    });
                    setLegalSearch(legalOption.name);
                    await generateLawArticleOptions(selectedLegalId);
                  }
                }}
              />
            </Box>
          </Flex>
        </Col>
        <Col span={12}>
          <Flex gap="middle" align="center">
            <Text text={t('or')} color="secondary" weight="regular" />
            <Button
              type="dashed"
              onClick={() => {
                appendLegalBasis({
                  id: t('no-id'),
                  number: '',
                  name: '',
                  article: '',
                  paragraph: '',
                  paragraphSubIndex: '',
                  isManualInput: true,
                });
              }}
              style={{ width: 240 }}
            >
              {t('add-manually')}
            </Button>
          </Flex>
        </Col>
      </Row>
    </Form.Item>
  );
};
